import { refreshAccessToken, setAuthToken, signOut } from 'api/apiClient'
import { addAxiosLoggingInterceptor } from 'api/axiosLoggingInterceptor'
import axios, { type AxiosRequestConfig } from 'axios'

import {
	AUTH_TOKEN_COOKIE,
	OVERRIDE_COUNTRY_CODE_HEADER,
	PMS_API_PREFIX,
} from '@/constants'
import { env, getCountryFromHeaders, isOnServer } from '@/utils'
import { getCookieClient } from '@/utils/client'

type RetriablePmsRequestConfig = AxiosRequestConfig & { _retry?: boolean }

// Same-origin path client-side: hits the /api/payments/v1 rewrite in
// legacy-app/next.config.js, so the browser attaches its cookies and any
// Cloudflare geo headers automatically, exactly like a same-domain request.
// Server-side (SSR), there is no browser request to ride along on, so we call
// the PMS host directly and forward cookies explicitly below. See APP-10708.
const pmsApiClient = axios.create({
	baseURL: isOnServer
		? `${env.PAYMENTS_API_URL}${PMS_API_PREFIX}`
		: PMS_API_PREFIX,
	timeout: 30_000,
	timeoutErrorMessage: 'Timeout Error',
	withCredentials: !isOnServer,
})

export const getPmsApiClient = () => pmsApiClient

if (isOnServer && env.ENABLE_AXIOS_LOGS_ON_SERVER) {
	addAxiosLoggingInterceptor(pmsApiClient)
}

// Client-side, `withCredentials: true` above sends the browser's full cookie
// jar automatically. SSR has no browser request to ride along on, so forward
// every incoming cookie explicitly instead of singling one out — the PMS may
// set and later expect cookies of its own, not just the auth token.
//
// PMS auth itself now goes over the Authorization header, not a cookie (per
// Payments team, 2026-08-07 — qrcreate-stage's PMS config switched from
// JWT_COOKIE_NAME to JWT_HEADER_NAME=Authorization). Attach the same
// auth_token as `Bearer <token>` on both client and server, alongside the
// existing cookie forwarding above.
//
// Server-side also has no browser attaching Cloudflare's geo headers, so
// forward cf-ipcountry/cf-ipcity explicitly — PMS resolves currency/provider
// account from these. Cloudflare in front of PAYMENTS_API_URL replaces
// cf-ipcountry with this server's own country, so the visitor's country also
// goes out as override--country-code, unless QA/automation already set one.
pmsApiClient.interceptors.request.use(async (config) => {
	config.headers = config.headers || {}

	if (isOnServer) {
		const { cookies, headers } = await import('next/headers')
		const cookieStore = await cookies()
		// cookies() decodes values, so re-encode before rebuilding the
		// header — a raw non-ASCII value makes Node throw ERR_INVALID_CHAR
		// on the whole Cookie header.
		const forwardedCookies = cookieStore
			.getAll()
			.map(({ name, value }) => `${name}=${encodeURIComponent(value)}`)
			.join('; ')

		if (forwardedCookies) {
			config.headers.Cookie = config.headers.Cookie
				? `${config.headers.Cookie}; ${forwardedCookies}`
				: forwardedCookies
		}

		const authToken = cookieStore.get(AUTH_TOKEN_COOKIE)?.value
		if (authToken) {
			config.headers.Authorization = `Bearer ${authToken}`
		}

		const incomingHeaders = await headers()
		const ipCountry = getCountryFromHeaders(incomingHeaders)
		if (ipCountry) {
			config.headers['cf-ipcountry'] = ipCountry
		}
		const ipCity = incomingHeaders.get('cf-ipcity')
		if (ipCity) {
			config.headers['cf-ipcity'] = ipCity
		}
		const overrideCountryCode =
			incomingHeaders.get(OVERRIDE_COUNTRY_CODE_HEADER) ?? ipCountry
		if (overrideCountryCode) {
			config.headers[OVERRIDE_COUNTRY_CODE_HEADER] = overrideCountryCode
		}
	} else {
		const authToken = getCookieClient(AUTH_TOKEN_COOKIE)
		if (authToken) {
			config.headers.Authorization = `Bearer ${authToken}`
		}
	}

	return config
})

// Unlike apiClient.ts, PMS has no refresh endpoint of its own — a 401 here
// just means qr-api's access token expired (same token both clients share
// via AUTH_TOKEN_COOKIE), so refresh it the same way apiClient.ts does and
// retry once. Refreshing needs the client-side auth store apiClient.ts's
// refreshAccessToken() reads, which has no server-side equivalent — SSR
// 401s pass through unchanged (short-lived page renders make hitting this
// mid-request unlikely there anyway, unlike a checkout tab left idle past
// the access token's TTL).
pmsApiClient.interceptors.response.use(
	(response) => response,
	async (error) => {
		const requestConfig = error?.config as
			| RetriablePmsRequestConfig
			| undefined
		const isUnauthorized =
			axios.isAxiosError(error) && error.response?.status === 401

		if (!isUnauthorized || isOnServer || requestConfig?._retry) {
			return Promise.reject(error)
		}

		const newToken = await refreshAccessToken()
		if (!newToken) {
			await signOut({ isRedirecting: false })
			return Promise.reject(error)
		}
		setAuthToken(newToken)

		const retriedConfig: RetriablePmsRequestConfig = {
			...requestConfig,
			_retry: true,
		}
		if (retriedConfig.headers) {
			retriedConfig.headers.Authorization = `Bearer ${newToken}`
		}
		return pmsApiClient(retriedConfig)
	},
)
